import { PLATFORM_LABEL, type Delivery } from "../lib/types";

const CHIP: Record<Delivery["status"], { cls: string; label: string }> = {
  pending:    { cls: "busy", label: "Queued" },
  validating: { cls: "busy", label: "Checking" },
  uploading:  { cls: "busy", label: "Uploading" },
  processing: { cls: "busy", label: "Processing" },
  retrying:   { cls: "warn", label: "Retrying" },
  published:  { cls: "ok",   label: "Published" },
  failed:     { cls: "err",  label: "Failed" },
  skipped:    { cls: "",     label: "Skipped" },
};

export function DeliveryRow({ d, onRetry, onDelete }: {
  d: Delivery;
  onRetry?: (id: string) => void;
  /** Absent when the viewer's role cannot remove a destination. */
  onDelete?: (d: Delivery) => void;
}) {
  const chip = CHIP[d.status];
  const privacy = d.provider_state?.privacy_status;

  return (
    <div className="target">
      <div className="body">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <div>
            <strong>{d.social_connections?.display_name ?? PLATFORM_LABEL[d.platform]}</strong>
            <span className="muted"> · {PLATFORM_LABEL[d.platform]}
              {d.placement !== "feed" ? ` (${d.placement})` : ""}</span>
          </div>
          <div className="row delivery-actions">
            <span className={`chip ${chip.cls}`}>{chip.label}</span>
            {onDelete && (
              <button
                className="sm danger"
                // Six rows on one page each offering "Delete" is a screen reader
                // reading out a guess, so the name carries the account and what
                // is being removed from.
                aria-label={`Remove ${d.social_connections?.display_name ?? PLATFORM_LABEL[d.platform]} from this post`}
                onClick={() => onDelete(d)}
              >
                Delete
              </button>
            )}
          </div>
        </div>

        {d.status === "processing" && (
          <div className="problem warning">
            {d.error_message ?? "Waiting for the platform to finish processing this media."}
          </div>
        )}

        {d.status === "failed" && (
          <div className="problem error">
            {d.error_message ?? "Publishing failed."}
            {d.attempt_count > 1 && <span className="muted"> ({d.attempt_count} attempts)</span>}
          </div>
        )}

        {d.status === "retrying" && (
          <div className="problem warning">
            {d.error_message} — attempt {d.attempt_count}, will try again.
          </div>
        )}

        {/* State the visibility YouTube actually applied, so it is never a
            surprise when the video is not where the publisher expects it. */}
        {d.status === "published" && d.platform === "youtube" &&
         (privacy === "private" || privacy === "unlisted") && (
          <div className="problem warning">
            Published as <strong>{privacy}</strong> on YouTube
            {privacy === "private"
              ? " — only you can see it."
              : " — only people with the link can see it."}
            {" "}Change this in YouTube Studio.
          </div>
        )}

        {d.provider_url && (
          <div style={{ marginTop: ".35rem" }}>
            <a href={d.provider_url} target="_blank" rel="noreferrer">View on {PLATFORM_LABEL[d.platform]}</a>
          </div>
        )}

        {onRetry && d.status === "failed" && !d.provider_post_id && (
          <button className="sm" style={{ marginTop: ".5rem" }} onClick={() => onRetry(d.id)}>
            Retry this network
          </button>
        )}
      </div>
    </div>
  );
}
